/**
 * Shared tally presentation — docs/08-frontend-and-ux.md §7.2.
 *
 * Two things the inbox and the detail page must say identically:
 *
 *  - the **seat-progress strip**: one dot per council seat, anonymous, capped at
 *    `active_seats`; and
 *  - the **decisive fact**, in words. §7.2 calls this arithmetic honesty — the
 *    panel always states what is actually true right now ("5 more approvals
 *    needed", "1 abstention recorded — this can no longer pass"), rather than
 *    leaving a reader to derive ADR-0006's clamped-unanimity arithmetic from a
 *    pair of numbers.
 */

import type { TFunction } from 'i18next';
import type { ProposalStatus, ProposalTally, VoteChoice } from '@famtree/shared';
import styles from './proposals.module.css';

const DOT_CLASS: Record<VoteChoice, string> = {
  approve: styles.dotApprove!,
  reject: styles.dotReject!,
  abstain: styles.dotAbstain!,
};

/**
 * The strip is built from counts, not from ballots: votes are family-public but
 * the *strip* is anonymous (§7.2), and the named list lives on the detail page.
 */
export function SeatStrip({ tally }: { tally: ProposalTally }): JSX.Element {
  const dots: VoteChoice[] = [
    ...Array<VoteChoice>(tally.approve).fill('approve'),
    ...Array<VoteChoice>(tally.reject).fill('reject'),
    ...Array<VoteChoice>(tally.abstain).fill('abstain'),
  ];

  return (
    <div className={styles.strip} aria-hidden="true">
      {dots.map((choice, index) => (
        <span key={`cast-${index}`} className={`${styles.dot} ${DOT_CLASS[choice]}`} />
      ))}
      {Array.from({ length: tally.not_voted }, (_, index) => (
        <span key={`empty-${index}`} className={`${styles.dot} ${styles.dotEmpty}`} />
      ))}
    </div>
  );
}

/** The numeric line under the strip, and the strip's accessible equivalent. */
export function tallySentence(tally: ProposalTally, t: TFunction): string {
  return t('proposals.tallyLine', {
    approve: tally.approve,
    threshold: tally.effective_threshold,
    reject: tally.reject,
    abstain: tally.abstain,
    notVoted: tally.not_voted,
  });
}

/**
 * The single most decisive true statement about an open proposal. Order matters:
 * un-approvability is the news when it happens, and it happens the instant a
 * rejection or (under unanimity) an abstention lands.
 */
export function decisiveFact(
  tally: ProposalTally,
  status: ProposalStatus,
  t: TFunction,
): string | null {
  if (status !== 'open') return null;
  if (!tally.approvable) {
    return tally.reject > 0
      ? t('proposals.fact.blockedByReject', { count: tally.reject })
      : t('proposals.fact.blockedByAbstain', {
          count: tally.abstain,
          threshold: tally.effective_threshold,
        });
  }
  const needed = tally.effective_threshold - tally.approve;
  if (needed <= 0) return t('proposals.fact.decidingNow');
  return t('proposals.fact.needed', { count: needed });
}

/**
 * `closes_at` as a countdown. §7.2 makes the last 48 hours visually distinct,
 * which is also when the worker's `vote_needed` reminders go out (docs/07 §9).
 */
export interface Countdown {
  label: string;
  urgent: boolean;
  elapsed: boolean;
}

export function countdown(closesAt: string, t: TFunction, now = Date.now()): Countdown {
  const remaining = new Date(closesAt).getTime() - now;
  if (remaining <= 0) return { label: t('proposals.closed'), urgent: false, elapsed: true };

  const hours = Math.floor(remaining / 3_600_000);
  const urgent = hours < 48;
  if (hours < 1) {
    return {
      label: t('proposals.closesInMinutes', { count: Math.max(1, Math.round(remaining / 60_000)) }),
      urgent,
      elapsed: false,
    };
  }
  if (hours < 48)
    return { label: t('proposals.closesInHours', { count: hours }), urgent, elapsed: false };
  return {
    label: t('proposals.closesInDays', { count: Math.round(hours / 24) }),
    urgent: false,
    elapsed: false,
  };
}

export function statusClass(status: ProposalStatus): string {
  switch (status) {
    case 'open':
      return styles.statusOpen!;
    case 'approved':
      return styles.statusApproved!;
    case 'rejected':
      return styles.statusRejected!;
    case 'expired':
      return styles.statusExpired!;
    default:
      return styles.statusWithdrawn!;
  }
}
